import * as Sentry from "@sentry/react-native";
import { Link } from "expo-router";
import { observer } from "mobx-react-lite";
import React, { useEffect, useRef, useState } from "react";
import {
  BackHandler,
  Dimensions,
  Image,
  Keyboard,
  Platform,
  ScrollView,
  StatusBar,
  StyleSheet,
  ToastAndroid,
} from "react-native";
import * as Animatable from "react-native-animatable";
import InCallManager from "react-native-incall-manager";
import Animated, {
  useAnimatedKeyboard,
  useAnimatedStyle,
  useSharedValue,
} from "react-native-reanimated";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { captureScreen } from "react-native-view-shot";
import { z } from "zod";
import { Button } from "~/components/Button";
import { Icon } from "~/components/Icon";
import { IconButton } from "~/components/IconButton";
import { Spacer } from "~/components/Spacer";
import { Text } from "~/components/Text";
import { TextInput } from "~/components/TextInput";
import { View } from "~/components/View";
import { useAlert } from "~/hooks/useAlert";
import { useAppState } from "~/hooks/useAppState";
import { state } from "~/store";
import { styleConstants } from "~/style/styleConstants";
import { assert } from "~/utils/assert";
import { delay } from "~/utils/delay";
import { useForceRender } from "../../hooks/useForceRender";
import { getLocaleURL } from "../intl";
import { Logo } from "../logo/Logo";
import { OtherUserOverlay } from "./OtherUserOverlay";
import { getAudioOptions } from "./getAudioOptions";
import { getTRPC } from "./tRPC";
import { useResponsive } from "./useResponsive";
import { RTCVideo } from "./web-rtc";
import type { MediaStream } from "./web-rtc";
import type { Socket } from "socket.io-client";

interface LiveVideoInProgressProps {
  resetConnectionId: () => void;
  socket: Socket;
  localStream: MediaStream;
  localStreamDimensions: { width: number; height: number };
  localUserDetails: {
    name: string;
    age: number;
    sex: "male" | "female" | "other";
  };
  remoteStream: null | MediaStream;
  remoteUserDetails: null | {
    name: string;
    age: number;
    sex: "male" | "female" | "other";
  };
}

export const LiveVideoInProgress = observer(function LiveVideoInProgress(
  props: LiveVideoInProgressProps,
) {
  const r = useResponsive();
  const alert = useAlert();
  const [chatMessageList, setChatMessageList] = useState<
    {
      id: string;
      message: string;
      isMine: boolean;
    }[]
  >([]);

  useEffect(
    function resetChatEffect() {
      if (props.remoteStream) {
        setChatMessageList([]);
      }
    },
    [props.remoteStream],
  );

  // We use this to prevent the user from tapping the button
  // multiple times. We effectively debounce the function for 300ms
  const isSendingEnabled = useRef(true);
  function sendChatMessage() {
    if (!isSendingEnabled.current) {
      return;
    }

    const message = chatMessage.trim();
    if (message === "") {
      Keyboard.dismiss();
      return;
    }

    isSendingEnabled.current = false;
    setTimeout(() => {
      isSendingEnabled.current = true;
    }, 300);
    setChatMessage("");
    setChatMessageList((list) => [
      ...list,
      {
        id: Math.random().toString(16).slice(-8),
        message: message,
        isMine: true,
      },
    ]);
    socket.emit("User sent chat message", {
      message: message,
    });
  }

  const socket = props.socket;
  useEffect(() => {
    socket.on("User sent chat message", (data: any) => {
      console.log("[🏞️] Got 'User sent chat message' event with data", data);

      const result = z
        .object({
          id: z.string().default(Math.random().toString(16).slice(-8)),
          message: z.string(),
          isMine: z.boolean().optional().default(false),
        })
        .safeParse(data);

      if (!result.success) {
        console.log(
          "[🏞️] Got 'User sent chat message' event with invalid data",
          data,
        );
        return;
      }

      setChatMessageList((list) => [...list, result.data]);
    });

    return () => {
      console.log("[🏞️] Cleaning up chat listeners");
      socket.off("User sent chat message");
    };
  }, [alert, socket]);

  // When we put the app in the background:
  //   On iOS:
  //     The local video and audio stream will freeze
  //   On Android:
  //     The local video will keep working
  //     The local audio will keep working for a couple of seconds, and then pause

  const localStream = props.localStream;
  const appState = useAppState();
  const isAppInForeground = appState === "active";
  useEffect(() => {
    if (!isAppInForeground) return;
    if (Platform.OS !== "android") return;

    // On Android, if we put the app in the background and then open native camera app,
    // the local stream will freeze. This is a workaround to fix that. We disable and enable
    // the video track to make the local stream work again.
    for (const track of localStream.getVideoTracks()) {
      track.enabled = false;
      track.enabled = true;
    }
  }, [isAppInForeground, localStream]);

  const isAppInBackground = appState === "background";
  useEffect(() => {
    if (!isAppInBackground) return;
    if (Platform.OS !== "android") return;

    ToastAndroid.show(
      intl.formatMessage({
        defaultMessage:
          "Video stream will continue while the app is in background",
        id: "SWSD0o",
        description:
          "Toast message on Android when the app is put in background",
      }),
      ToastAndroid.LONG,
    );
  }, [isAppInBackground, localStream]);

  useEffect(function preventBackButtonEffect() {
    // On Android prevent hardware back button from closing the app
    const listener = BackHandler.addEventListener("hardwareBackPress", () => {
      return true;
    });

    return () => {
      listener.remove();
    };
  }, []);

  const forceRender = useForceRender();

  const isLocalMicOn = localStream.getAudioTracks().at(0)?.enabled ?? true;

  const localStreamDimensions = props.localStreamDimensions;

  const areDimensionsValid =
    localStreamDimensions.width > 0 && localStreamDimensions.height > 0;

  const localStreamWidth = r(100, 150, 200);
  const localStreamHeight = areDimensionsValid
    ? localStreamWidth *
      (localStreamDimensions.height / localStreamDimensions.width)
    : localStreamWidth * (16 / 9);

  // const [cameraFacingMode, setCameraFacingMode] = useState<
  //   "user" | "environment"
  //   // @ts-expect-error This is an ugly hack. We set __facingMode when we first grab the
  //   // localStream and use it to keep track of facing mode.
  // >(localStream.__facingMode);

  const localUserDetails = props.localUserDetails;
  const remoteUserDetails = props.remoteUserDetails;

  useEffect(function initializeAudioOutputEffect() {
    const cleanupList: (() => void)[] = [];
    async function runInitializeAudioOutputEffect() {
      try {
        if (Platform.OS === "web") return;

        InCallManager.start({ media: "video" });
        cleanupList.push(() => {
          InCallManager.stop();
        });

        await delay(3000);

        const audioOptions = getAudioOptions();
        const audioOutput =
          state.preferredAudioOutput ?? audioOptions.preferred;

        if (audioOutput === "BLUETOOTH") {
          await InCallManager.chooseAudioRoute("BLUETOOTH");
        } else if (audioOutput === "WIRED_HEADSET") {
          InCallManager.setForceSpeakerphoneOn(false);
        } else {
          InCallManager.setForceSpeakerphoneOn(true);
        }
      } catch (error) {
        console.log(error);
        // TODO: Maybe show error message to the user?
      }
    }

    runInitializeAudioOutputEffect();

    return () => {
      for (const cleanup of cleanupList) {
        cleanup();
      }
    };
  }, []);

  const [remoteStreamObjectFit, setRemoteStreamObjectFit] = useState<
    "cover" | "contain"
  >(Platform.OS === "web" ? r("cover", "contain", "contain") : "cover");

  const [chatMessage, setChatMessage] = useState("");

  async function toggleMicMuteUnmute() {
    for (const track of localStream.getAudioTracks()) {
      track.enabled = !track.enabled;
    }
    forceRender();

    // We wait a bit so the mic can mute/unmute
    await delay(500);
  }

  async function toggleCameraFacingMode() {
    if (Platform.OS === "web") {
      return;
    }

    for (const track of localStream.getVideoTracks()) {
      // This works only in the React Native implementation
      track._switchCamera();
      // @ts-expect-error We track facing mode in `__facingMode`
      track.__facingMode =
        // @ts-expect-error We track facing mode in `__facingMode`
        track.__facingMode === "user" ? "environment" : "user";
    }
    forceRender();

    // We wait a bit so the camera can switch
    await delay(500);
  }

  const facingMode: "user" | "environment" | undefined =
    // @ts-expect-error .facingMode is only available on mobile
    localStream.getVideoTracks().at(0)?.__facingMode;

  const scrollViewRef = useRef<ScrollView>(null);

  const insets = useSafeAreaInsets();

  const animatedKeyboard = useAnimatedKeyboard();

  const heightUnderKeyboard = useSharedValue(0);
  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateY: -Math.max(
            animatedKeyboard.height.value - heightUnderKeyboard.value,
            0,
          ),
        },
      ],
    };
  });

  async function report() {
    // getScreenshot and promptUser are defined here and immediately called in
    // this function.
    async function getScreenshot() {
      if (Platform.OS === "web") {
        const videos = document.getElementsByTagName("video");
        const videoA = videos[0];
        const videoB = videos[1];
        assert(!!videoA, "No videoA");
        assert(!!videoB, "No videoB");
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");

        assert(!!context, "Canvas context should be available");

        // Set the canvas width
        canvas.width = 800;

        // Calculate the width for each video to take half of the canvas
        const halfCanvasWidth = 400;

        // Calculate the aspect ratio for each video
        const aspectRatioA = videoA.videoWidth / videoA.videoHeight;
        const aspectRatioB = videoB.videoWidth / videoB.videoHeight;

        // Calculate the height for each video based on the half canvas width and its aspect ratio
        const heightA = halfCanvasWidth / aspectRatioA;
        const heightB = halfCanvasWidth / aspectRatioB;

        // Determine the maximum height between the two videos
        const maxHeight = Math.max(heightA, heightB);

        // Set the canvas height to the maximum height if it's less than 800
        canvas.height = Math.min(maxHeight, 800);

        // Draw the videos on the canvas, each taking half of the canvas width and maintaining aspect ratio
        context.drawImage(videoA, 0, 0, halfCanvasWidth, heightA);
        context.drawImage(videoB, halfCanvasWidth, 0, halfCanvasWidth, heightB);

        const screenshotBase64DataURI = canvas.toDataURL("image/jpeg", 0.9);
        return screenshotBase64DataURI;
      }

      const maxSize = 800;
      const windowDims = Dimensions.get("window");

      let width: number;
      let height: number;

      if (windowDims.width > windowDims.height) {
        width = maxSize;
        height = Math.round((windowDims.height / windowDims.width) * maxSize);
      } else {
        width = Math.round((windowDims.width / windowDims.height) * maxSize);
        height = maxSize;
      }

      // On Android, sometimes the capture fails. We retry a few times
      // since the capture seems to work fine on second or third try
      // Also, we don't want to retry indefinitely, so we limit the number of tries
      let numberOfTriesLeft = 5;
      while (numberOfTriesLeft > 0) {
        numberOfTriesLeft--;
        try {
          // This await is necessary, do not remove it.
          return await captureScreen({
            format: "jpg",
            quality: 0.75,
            result: "data-uri",
            width: width,
            height: height,
            handleGLSurfaceViewOnAndroid: true,
          });
        } catch (error) {
          console.log(
            `Error capturing screen. Tries left: ${numberOfTriesLeft}`,
            error,
          );
          if (numberOfTriesLeft === 0) {
            throw error;
          }
        }
      }
      throw new Error("Unreachable code");
    }

    function promptUser() {
      return new Promise<"block" | "block-and-report" | "cancel">((resolve) => {
        alert(
          intl.formatMessage({
            defaultMessage: "What would you like to do?",
            id: "4Zt56V",
            description:
              "Title in pop-up when pressing the report button in a video chat",
          }),
          (
            <Text alignLeft sizeMedium>
              {intl.formatMessage(
                {
                  defaultMessage:
                    "You can choose to <b>block</b> this user to stop them from contacting you, or <b>block and report</b> them if you believe they’ve violated our guidelines.",
                  id: "gH39gO",
                  description:
                    "Message in pop-up when pressing the report button in a video chat",
                },
                {
                  b: (text: string) => (
                    <Text weightExtraBold alignLeft sizeMedium>
                      {text}
                    </Text>
                  ),
                } as any,
              )}
            </Text>
          ) as any,
          [
            {
              text: intl.formatMessage({
                defaultMessage: "Block",
                id: "bjCPmy",
                description: "Button to block user",
              }),
              onPress: () => resolve("block"),
            },
            {
              text: intl.formatMessage({
                defaultMessage: "Block and Report",
                id: "Vjas27",
                description: "Button to block and report user",
              }),
              onPress: () => resolve("block-and-report"),
            },
            {
              text: intl.formatMessage({
                defaultMessage: "Cancel",
                id: "w6fSfw",
                description: "Button to cancel reporting user",
              }),
              onPress: () => resolve("cancel"),
              style: "cancel",
            },
          ],
          undefined,
          {
            cancelable: true,
            onDismiss: () => resolve("cancel"),
          },
        );
      });
    }

    // This is not awaited on purpose, so that the user can still interact with
    // the app
    const screenshotPromise = getScreenshot();

    const action = await promptUser();

    if (action === "cancel") {
      return;
    }

    socket.emit("User pressed report");

    const reportId = await new Promise<number | undefined>((resolve) => {
      const timeoutId = setTimeout(() => {
        socket.off("Report ID");
        resolve(undefined);
      }, 4000);

      socket.off("Report ID"); // Just in case there was an old listener hanging around
      socket.once("Report ID", (reportId) => {
        assert(typeof reportId === "number", "Report ID should be a number");
        clearTimeout(timeoutId);
        resolve(reportId);
      });
    });

    if (reportId === undefined) {
      Sentry.captureException(
        new Error("Live chat - Report ID was not received from the server"),
      );
    } else {
      const screenshotBase64DataURI = await screenshotPromise;

      const tRPC = getTRPC();

      await tRPC.updateReportImage.mutate({
        reportId: reportId,
        image: screenshotBase64DataURI,
      });
    }

    // Sometimes the reportId might not be received from the server
    // in this case we lie to the user and tell them that the report
    // has been sent and just skip to the next match.
    alert(
      intl.formatMessage({
        defaultMessage: "Success",
        id: "yg2HFT",
        description: "Success",
      }),
      action === "block-and-report"
        ? intl.formatMessage({
            defaultMessage:
              "The user has been blocked and reported. You will not be able to connect with them again. Thank you for helping us keep the community safe.",
            id: "1Vuu4c",
            description:
              "Success message when we block and report a user in the live chat",
          })
        : intl.formatMessage({
            defaultMessage:
              "The user has been blocked. You will not be able to connect with them again.",
            id: "szpcPk",
            description:
              "Success message when we block a user in the live chat",
          }),
    );

    socket.emit("User pressed next");
    props.resetConnectionId();
    // This is to prevent the user from tapping the button
    // multiple times. Making the app seem more stable
    await delay(2000);
  }

  return (
    <View flex>
      {Platform.OS === "ios" && !!remoteUserDetails && props.remoteStream && (
        <OtherUserOverlay
          otherUserDetails={remoteUserDetails}
          remoteStream={props.remoteStream}
          onDecline={async function onUserDeclined() {
            socket.emit("User pressed next");
            props.resetConnectionId();
            // This is to prevent the user from tapping the button
            // multiple times. Making the app seem more stable
            await delay(300);
          }}
        />
      )}
      {/**
       * Setting a translucent status bar makes has an additional behaviour
       * change on Android. When the keyboard is visible it doesn't push the
       * viewport up, which we want.
       */}
      <StatusBar
        barStyle="light-content"
        backgroundColor={styleConstants.colorBackgroundDarkLight}
        hidden={false}
        translucent
        animated
      />
      {/* This view renders behind the status bar */}
      <View style={{ height: insets.top }} colorBackgroundDarkLight />

      <View flex style={{ flexDirection: r("column", "column", "row") }}>
        <View flex>
          <View
            flexDirectionRow
            alignItemsCenter
            colorBackgroundDarkLight
            style={{
              // backgroundColor: r("blue", "coral", "pink"),
              paddingHorizontal: r(8, 16, 32),
              height: r(48, 64, 72),
            }}
          >
            <Logo size={r(32, 48, 54)} />

            <View style={{ width: r(8, 20, 20) }} />
            <Text
              style={{
                fontSize: r(22, 28, 34),
                lineHeight: undefined,
              }}
              weightExtraBold
              colorLight
            >
              {intl.formatMessage({
                defaultMessage: "BokBok Live",
                id: "qMC/dU",
                description:
                  "Header - BokBok Live - Do not translate unless really necessary",
              })}
            </Text>

            <View flex />

            <Link asChild href={getLocaleURL("/")} replace>
              <Button
                small={r(true, false, false)}
                large={r(false, false, true)}
                colorLight
              >
                <Text
                  weightSemiBold
                  colorTheme
                  style={{
                    fontSize: r(
                      styleConstants.fontSizeMedium,
                      styleConstants.fontSizeMedium,
                      styleConstants.fontSizeLarge,
                    ),
                    lineHeight: undefined,
                  }}
                >
                  {intl.formatMessage({
                    defaultMessage: "Stop",
                    id: "nOohz8",
                    description: "Button to stop the call",
                  })}
                </Text>
                <Spacer small />
                <Icon
                  name="stop"
                  color={styleConstants.colorTextTheme}
                  size={24}
                />
              </Button>
            </Link>

            <Spacer medium />

            <Button
              disabled={props.remoteStream === null}
              small={r(true, false, false)}
              large={r(false, false, true)}
              colorLight
              onPress={async function leave() {
                socket.emit("User pressed next");
                props.resetConnectionId();
                // This is to prevent the user from tapping the button
                // multiple times. Making the app seem more stable
                await delay(2000);
              }}
            >
              <Text
                style={{
                  fontSize: r(
                    styleConstants.fontSizeMedium,
                    styleConstants.fontSizeMedium,
                    styleConstants.fontSizeLarge,
                  ),
                  lineHeight: undefined,
                }}
                weightSemiBold
                colorTheme
              >
                {intl.formatMessage({
                  defaultMessage: "Skip",
                  id: "qcQ9Qo",
                  description:
                    "Button to skip the call and go to the next call",
                })}
              </Text>
              <Spacer small />
              <Icon
                name="forward"
                color={styleConstants.colorTextTheme}
                size={24}
              />
            </Button>
          </View>

          <View
            flexDirectionRow
            alignItemsCenter
            paddingHorizontalMedium
            paddingVerticalExtraSmall
            colorBackgroundDarkLight
            style={{
              display: r("flex", "flex", "none"),
            }}
          >
            <Text
              colorLight
              weightExtraBold
              sizeSmall
              numberOfLines={1}
              alignRight
            >
              {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
              {localUserDetails.name}, {localUserDetails.age}
            </Text>
            <Spacer small />
            <Icon
              size={18}
              color={styleConstants.colorTextLight}
              name={
                localUserDetails.sex === "male"
                  ? "face-man"
                  : localUserDetails.sex === "female"
                    ? "face-woman"
                    : "human-non-binary"
              }
            />

            {/* This element is used as a spacer that fills the middle */}
            <View style={{ minWidth: styleConstants.spacingExtraLarge }} flex />

            <Text numberOfLines={1} alignLeft colorLight sizeSmall>
              {!remoteUserDetails
                ? intl.formatMessage({
                    defaultMessage: "Looking for a match...",
                    id: "Y2Ui7F",
                    description:
                      "Looking for a match text on LiveVideoChatScreen",
                  })
                : intl.formatMessage(
                    {
                      defaultMessage:
                        "<normal>Connected to: </normal><bold>{name}, {age}</bold>",
                      id: "BZG0f6",
                      description: "Label for 'connected to' user.",
                    },
                    {
                      name: remoteUserDetails.name,
                      age: remoteUserDetails.age,
                      normal(text) {
                        return (
                          r(false, true, true) && (
                            <Text colorLight sizeSmall>
                              {text}
                            </Text>
                          )
                        );
                      },
                      bold(text) {
                        return (
                          <Text
                            colorLight
                            weightExtraBold
                            sizeSmall
                            numberOfLines={1}
                            alignLeft
                          >
                            {text}
                          </Text>
                        ) as any;
                      },
                    },
                  )}
            </Text>

            <Spacer small />
            {remoteUserDetails && (
              <Icon
                size={18}
                color={styleConstants.colorTextLight}
                name={
                  remoteUserDetails.sex === "male"
                    ? "face-man"
                    : remoteUserDetails.sex === "female"
                      ? "face-woman"
                      : "human-non-binary"
                }
              />
            )}
          </View>

          <View flex colorBackgroundLightDarker>
            <>
              {/* 
                Video element behaves weirdly on web (I'm guessing kind of min-height issue)
                so we position it absolutely inside a view with flex: 1, and then it works
                 */}
              <View flex>
                {props.remoteStream ? (
                  <RTCVideo
                    objectFit={remoteStreamObjectFit}
                    stream={props.remoteStream}
                    style={styles.videoStream}
                  />
                ) : (
                  <View style={styles.videoStream} centerContent>
                    <Animatable.View
                      animation={{
                        0: { transform: [{ scale: 1 }, { rotate: "-10deg" }] },
                        0.5: {
                          transform: [{ scale: 1.3 }, { rotate: "0deg" }],
                        },
                        1: { transform: [{ scale: 1 }, { rotate: "-10deg" }] },
                      }}
                      duration={1000}
                      easing="ease-in-out-sine"
                      iterationCount="infinite"
                      useNativeDriver
                    >
                      <Logo size={64} />
                    </Animatable.View>

                    <Spacer extraLarge />
                    <Text sizeExtraLarge weightSemiBold colorTheme>
                      {intl.formatMessage({
                        id: "Y2Ui7F",
                        defaultMessage: "Looking for a match...",
                        description:
                          "Looking for a match text on LiveVideoChatScreen",
                      })}
                    </Text>
                  </View>
                )}
              </View>
            </>

            <View absoluteTopLeftExtraLarge>
              <IconButton
                disabled={props.remoteStream === null}
                iconColor={styleConstants.colorTextLight}
                iconSize={24}
                iconName={
                  remoteStreamObjectFit === "cover"
                    ? "fullscreen-exit"
                    : "fullscreen"
                }
                onPress={function onFullscreenPress() {
                  setRemoteStreamObjectFit((value) =>
                    value === "cover" ? "contain" : "cover",
                  );
                }}
              />

              {localStream.getTracks().length > 0 && (
                <>
                  <Spacer large />

                  <IconButton
                    iconColor={styleConstants.colorTextLight}
                    iconSize={24}
                    iconName={isLocalMicOn ? "mic-on" : "mic-off"}
                    onPress={toggleMicMuteUnmute}
                  />
                </>
              )}

              {Platform.OS !== "web" && (
                <>
                  <Spacer large />

                  <IconButton
                    iconColor={styleConstants.colorTextLight}
                    iconSize={24}
                    iconName="camera-flip"
                    onPress={toggleCameraFacingMode}
                  />
                </>
              )}

              <Spacer large />

              <IconButton
                disabled={props.remoteStream === null}
                iconColor={styleConstants.colorTextLight}
                iconSize={24}
                iconName="report"
                onPress={report}
              />
            </View>

            <View
              style={{
                position: "absolute",
                right: 4,
                top: 4,
                width: localStreamWidth,
                height: localStreamHeight,
                borderRadius: styleConstants.borderRadiusMedium,
                overflow: "hidden",
              }}
            >
              <View flex style={{ zIndex: 10 }}>
                <RTCVideo
                  mirror={facingMode === "user"}
                  stream={props.localStream}
                  muted
                  objectFit="cover"
                  style={styles.videoStream}
                  zOrder={1}
                />
              </View>
            </View>
          </View>
        </View>

        <View style={{ width: r("100%", "100%", 540) }}>
          <View
            flexDirectionRow
            colorBackgroundDarkLight
            alignItemsCenter
            justifyContentFlexEnd
            style={{
              display: r("none", "none", "flex"),
              height: r(48, 64, 72),
              paddingHorizontal: r(16, 16, 40),
              paddingVertical: r(24, 24, 24),
            }}
          >
            <Text
              sizeLarge={r(false, false, true)}
              sizeSmall={r(true, true, false)}
              colorLight
              alignRight
            >
              {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
              {localUserDetails.name}, {localUserDetails.age}
            </Text>
            <Spacer small />
            <Icon
              size={24}
              color={styleConstants.colorTextLight}
              name={
                localUserDetails.sex === "male"
                  ? "face-man"
                  : localUserDetails.sex === "female"
                    ? "face-woman"
                    : "human-non-binary"
              }
            />
          </View>

          {/* Chat - only visible on desktop */}
          <View
            flex
            style={{
              display: r("none", "none", "flex"),
            }}
          >
            <View
              flexDirectionRow
              alignItemsCenter
              paddingHorizontalLarge
              paddingVerticalSmall
              colorBackgroundDarkLighter
              style={{ minHeight: 40 }}
            >
              <Text colorLight>
                {!remoteUserDetails
                  ? intl.formatMessage({
                      defaultMessage: "Looking for a match...",
                      id: "Y2Ui7F",
                      description:
                        "Looking for a match text on LiveVideoChatScreen",
                    })
                  : intl.formatMessage(
                      {
                        defaultMessage:
                          "Connected to <normal>{name}, {age}</normal>",
                        id: "VVu/rJ",
                        description: "Label for 'connected to' user.",
                      },
                      {
                        name: remoteUserDetails.name,
                        age: remoteUserDetails.age,
                        normal(text) {
                          return (
                            <Text colorLight weightExtraBold>
                              {text}
                            </Text>
                          ) as any;
                        },
                      },
                    )}
              </Text>
              <Spacer small />
              {remoteUserDetails && (
                <Icon
                  size={18}
                  color={styleConstants.colorTextLight}
                  name={
                    remoteUserDetails.sex === "male"
                      ? "face-man"
                      : remoteUserDetails.sex === "female"
                        ? "face-woman"
                        : "human-non-binary"
                  }
                />
              )}
            </View>

            {/*  Chat messages */}
            {/* Handled cases when there's a lot of messages so it scrolls */}
            <View style={{ flex: 1, flexBasis: 0 }}>
              <Image
                source={require("./tile.png")}
                resizeMode="repeat"
                style={{
                  ...StyleSheet.absoluteFillObject,
                  width: 540,
                  minHeight: 540,
                  height: "100%",
                }}
              />

              <ScrollView
                ref={scrollViewRef}
                contentContainerStyle={{ flexGrow: 1 }}
                onContentSizeChange={() => {
                  scrollViewRef.current?.scrollToEnd();
                }}
              >
                {/* Messages */}
                <View
                  flex
                  justifyContentFlexEnd
                  style={{ gap: 4, paddingVertical: 10 }}
                >
                  {chatMessageList.map((message) => {
                    return (
                      <View
                        key={message.id.toString()}
                        style={{
                          paddingLeft: message.isMine ? 40 : 80,
                          paddingRight: message.isMine ? 80 : 40,
                        }}
                        alignSelfFlexStart={message.isMine}
                        alignSelfFlexEnd={!message.isMine}
                      >
                        <View
                          borderRadiusMedium
                          colorBackgroundDarkLight
                          style={{ paddingHorizontal: 10, paddingVertical: 6 }}
                        >
                          <Text colorLight>{message.message}</Text>
                        </View>
                      </View>
                    );
                  })}
                </View>
              </ScrollView>
            </View>
          </View>
          {/* End chat */}

          {/* Mobile only chat messages */}
          <Animated.View
            style={[
              {
                pointerEvents: "none",
                display: r("flex", "flex", "none"),
                position: "absolute",
                bottom: 64,
                left: 0,
                right: 0,
              },
              animatedStyle,
            ]}
          >
            {/* We only show 6 last messages on mobile, and older messages have less opacity */}
            {chatMessageList.slice(-6).map((message, index, list) => {
              const indexFromBottom = list.length - 1 - index;

              return (
                <View
                  key={message.id.toString()}
                  style={{
                    paddingHorizontal: r(8, 16, 24),
                    paddingVertical: 2,
                    opacity:
                      indexFromBottom === 0
                        ? 1
                        : indexFromBottom === 1
                          ? 1
                          : indexFromBottom === 2
                            ? 1
                            : indexFromBottom === 3
                              ? 1
                              : indexFromBottom === 4
                                ? 0.75
                                : indexFromBottom === 5
                                  ? 0.25
                                  : 0.1,
                  }}
                >
                  <Text
                    colorLight
                    weightSemiBold
                    style={{
                      textShadowColor: styleConstants.colorBackgroundDark,
                      textShadowOffset: { width: 0, height: 0 },
                      textShadowRadius: 5,
                    }}
                  >
                    <Text
                      style={{
                        color: message.isMine
                          ? styleConstants.colorBackgroundLightDark
                          : styleConstants.colorBackgroundDarkLight,
                        textShadowColor: message.isMine
                          ? styleConstants.colorBackgroundDark
                          : styleConstants.colorBackgroundLight,
                        textShadowOffset: { width: 0, height: 0 },
                        textShadowRadius: 7,
                      }}
                      weightExtraBold
                    >
                      {message.isMine
                        ? intl.formatMessage({
                            defaultMessage: "Me",
                            id: "En5mF4",
                            description:
                              "Label when we are the chat message sender",
                          })
                        : intl.formatMessage({
                            defaultMessage: "Partner",
                            id: "sR1GMF",
                            description:
                              "Label when chat message sender is the other party",
                          })}
                      {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                    </Text>{" "}
                    {message.message}
                  </Text>
                </View>
              );
            })}
          </Animated.View>

          {/* Text input for chat */}
          <Animated.View
            style={[
              {
                flexDirection: "row",
                alignItems: "center",
                paddingHorizontal: r(8, 16, 24),
                paddingVertical: r(8, 16, 16),
              },
              r(
                [
                  {
                    backgroundColor: "transparent",
                    position: "absolute",
                    left: 0,
                    bottom: 0,
                    right: 0,
                  },
                  animatedStyle,
                ],
                [
                  {
                    backgroundColor: "transparent",
                    position: "absolute",
                    left: 0,
                    bottom: 0,
                    right: 0,
                  },
                  animatedStyle,
                ],
                {
                  backgroundColor: styleConstants.colorBackgroundDarkLight,
                },
              ),
            ]}
          >
            <View flex>
              <TextInput
                editable={!!props.remoteStream}
                maxLength={180}
                value={chatMessage}
                onChangeText={setChatMessage}
                placeholder={intl.formatMessage({
                  defaultMessage: "Type a message...",
                  id: "RdwX+e",
                  description: "Text input placeholder",
                })}
                style={[
                  {
                    pointerEvents: props.remoteStream ? "auto" : "none",
                    minHeight: r(36, 36, 48),
                    borderRadius: r(18, 18, 24),
                  },
                  Platform.OS === "web" ? { ["outlineWidth" as any]: 0 } : {},
                ]}
                containerProps={{
                  style: {
                    opacity: props.remoteStream ? 1 : 0.5,
                    flexDirection: "row",
                    backgroundColor: styleConstants.colorBackgroundLight,
                    minHeight: r(36, 36, 48),
                    borderRadius: r(18, 18, 24),
                  },
                }}
                blurOnSubmit={false}
                onSubmitEditing={sendChatMessage}
              />
            </View>

            <Spacer medium />

            <IconButton
              disabled={props.remoteStream === null}
              iconName="send"
              iconColor={styleConstants.colorTextTheme}
              iconSize={24}
              onPress={sendChatMessage}
              centerContent
              style={{
                opacity: props.remoteStream ? 1 : 0.5,
                backgroundColor: styleConstants.colorBackgroundLight,
                borderRadius: r(18, 18, 24),
                height: r(36, 36, 48),
                width: r(36, 36, 48) * 1.5,
              }}
            />
          </Animated.View>
        </View>
      </View>

      <View
        onLayout={(e) => {
          heightUnderKeyboard.value = e.nativeEvent.layout.height;
        }}
        flexDirectionRow
        colorBackgroundDarkLight
        style={{ gap: 32 }}
        paddingHorizontalExtraLarge={r(false, true, true)}
        paddingHorizontalSmall={r(true, false, false)}
        paddingVerticalExtraSmall
      >
        <Link href="https://bokbok.io/guidelines" target="_blank">
          <Text
            numberOfLines={1}
            colorLightSoft
            weightSemiBold
            sizeExtraSmall={r(true, false, false)}
            sizeMedium={r(false, true, true)}
          >
            {intl.formatMessage({
              defaultMessage: "Rules",
              id: "t1Pz6Q",
              description: "Footer link",
            })}
          </Text>
        </Link>
        <Link href="https://bokbok.io/privacy-policy" target="_blank">
          <Text
            numberOfLines={1}
            colorLightSoft
            weightSemiBold
            sizeExtraSmall={r(true, false, false)}
            sizeMedium={r(false, true, true)}
          >
            {intl.formatMessage({
              defaultMessage: "Privacy Policy",
              id: "H8KCFi",
              description: "Footer link",
            })}
          </Text>
        </Link>
        <Link href="https://bokbok.io/terms-of-services" target="_blank">
          <Text
            numberOfLines={1}
            colorLightSoft
            weightSemiBold
            sizeExtraSmall={r(true, false, false)}
            sizeMedium={r(false, true, true)}
          >
            {intl.formatMessage({
              defaultMessage: "ToS",
              id: "H0Dk78",
              description: "Footer link",
            })}
          </Text>
        </Link>
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  videoStream: {
    ...StyleSheet.absoluteFillObject,
    width: "100%",
    height: "100%",
    pointerEvents: "none",
  },
});
